import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Award, GraduationCap, Briefcase } from 'lucide-react';

export const ManagerSection = () => {
  return (
    <section id="manager" className="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-green-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 mb-6">
            Meet Your <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">Fund Manager</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Led by an industry veteran with exceptional qualifications and proven track record.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Manager Profile */}
          <Card className="bg-white/80 backdrop-blur-sm shadow-2xl border-blue-100">
            <CardHeader>
              <div className="flex items-center space-x-6">
                <div className="w-24 h-24 rounded-full p-1 bg-gradient-to-br from-blue-500 to-green-500 shadow-lg">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_index-wealth-1/artifacts/dwq8719b_anuraag_photo.jpg"
                    alt="Mr. Anuraag Saboo - Chief Investment Officer"
                    className="w-full h-full object-cover rounded-full"
                    style={{
                      objectPosition: 'center 20%'
                    }}
                  />
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold text-slate-800">Mr. Anuraag Saboo</CardTitle>
                  <p className="text-lg text-blue-600 font-semibold mt-1">Chief Investment Officer</p>
                  <p className="text-slate-600 mt-2">29+ Years of Investment Experience</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Educational Background */}
              <div>
                <h4 className="text-lg font-semibold text-slate-800 mb-3 flex items-center">
                  <GraduationCap className="w-5 h-5 mr-2 text-blue-600" />
                  Educational Excellence
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                      Gold Medalist
                    </Badge>
                    <p className="text-slate-600">B.Tech from IIT Madras (Top of Class)</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Badge variant="secondary" className="bg-green-100 text-green-700">
                      PGDM
                    </Badge>
                    <p className="text-slate-600">Finance/Strategy from IIM Bangalore</p>
                  </div>
                </div>
              </div>

              {/* Professional Certifications */}
              <div>
                <h4 className="text-lg font-semibold text-slate-800 mb-3 flex items-center">
                  <Award className="w-5 h-5 mr-2 text-green-600" />
                  Professional Certifications
                </h4>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="font-semibold text-blue-800">CMT</p>
                    <p className="text-sm text-slate-600">Certified Market Technician (CMT Association, USA)</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="font-semibold text-green-800">CFP</p>
                    <p className="text-sm text-slate-600">Chartered Financial Planner (FPSB, USA)</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="font-semibold text-blue-800">CFTe</p>
                    <p className="text-sm text-slate-600">Chartered Financial Technician (IFTA, UK)</p>
                  </div>
                </div>
              </div>

              {/* Professional Experience */}
              <div>
                <h4 className="text-lg font-semibold text-slate-800 mb-3 flex items-center">
                  <Briefcase className="w-5 h-5 mr-2 text-blue-600" />
                  Professional Journey
                </h4>
                <div className="space-y-4">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h5 className="font-semibold text-slate-800">Kotak Securities Limited</h5>
                    <p className="text-sm text-blue-600 font-medium">Senior Executive Vice President</p>
                    <p className="text-sm text-slate-600 mt-1">Led FundExpert / Kotak Connect Plus Vertical - Business and Product Development</p>
                  </div>
                  <div className="border-l-4 border-green-500 pl-4">
                    <h5 className="font-semibold text-slate-800">FundExpert Fintech</h5>
                    <p className="text-sm text-green-600 font-medium">Co-Founder Director</p>
                    <p className="text-sm text-slate-600 mt-1">Digital multi-asset wealth software product development</p>
                  </div>
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h5 className="font-semibold text-slate-800">Gumption Labs</h5>
                    <p className="text-sm text-blue-600 font-medium">Co-Founder</p>
                    <p className="text-sm text-slate-600 mt-1">Markets analytics domain expertise and innovation</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quote Section */}
          <div className="mt-8">
            <Card className="bg-gradient-to-br from-blue-50 to-green-50 border-blue-200 shadow-xl">
              <CardContent className="pt-6">
                <blockquote className="text-center">
                  <p className="text-slate-700 italic mb-4 text-lg">
                    "Combining decades of experience with systematic, rule-based investment strategies to deliver consistent value for our investors."
                  </p>
                  <footer className="text-sm text-slate-500">
                    — Anuraag Saboo, CIO
                  </footer>
                </blockquote>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};